import { Table as UITable } from "@/components/ui/table";
import { useTableScrollAndResize } from "@/hooks/useTableScrollAndResize";
import { cn } from "@/lib/utils";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { getNextColumnOrderOnDrag } from "./utils/getNextColumnOrderOnDrag";
import ColumnResizeHandleProvider from "./components/ColumnResizeHandleProvider";
import {
  DataTableBody,
  MemoizedDataTableBody,
} from "./components/DataTableBody";
import DataTableDraggedColumnOverlay from "./components/DataTableDraggedColumnOverlay";
import DataTableHeader from "./components/DataTableHeader";
import DataTableResizedColumnOverlay from "./components/DataTableResizedColumnOverlay";
import { DataTableActiveFiltersBar } from "./components/DataTableActiveFiltersBar/DataTableActiveFiltersBar";
import EmptyState from "./components/EmptyState";
import { useDataTableSettings } from "./hooks/useDataTableSettings";
import useTableColumnSizeAndPositionStyle from "./hooks/useTableColumnSizeAndPositionStyle";
import { CopySelectionProvider } from "./context/CopySelectionContext";
import { useDataTableContextMenu } from "./hooks/useDataTableContextMenu";
import useTableCopySelection from "./hooks/useTableCopySelection";
import { useDataTableQuery } from "@/components/data-table/hooks/useDataTableQuery";
import { DataTableQuery } from "@/types/dataTableQuery";

interface DataTableProps<_TData> {
  rowClassName?: string;
  headerOffset?: number;
  className?: string;
  onResize?: (rect: DOMRectReadOnly) => void;
  onScroll?: (scrollLeft: number, scrollTop: number) => void;
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
  footer?: React.ReactNode;
  isInDialog?: boolean;
  getRowProps?: <T>(row: { original: T }) => Record<string, unknown>;
}

interface TData {
  [key: string]: unknown;
  id: string;
}

interface WithDataTableRefType
  extends React.ForwardRefExoticComponent<
    Omit<DataTableProps<TData>, "ref"> & React.RefAttributes<HTMLDivElement>
  > {
  <TData>(
    props: DataTableProps<TData> & React.RefAttributes<HTMLDivElement>,
  ): ReturnType<
    React.FC<DataTableProps<TData> & React.RefAttributes<HTMLDivElement>>
  >;
}

const DataTable: WithDataTableRefType = forwardRef<
  HTMLDivElement,
  DataTableProps<TData>
>((props, ref) => {
  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const {
    className,
    onResize,
    onScroll,
    onRowMouseEnter,
    onRowMouseLeave,
    footer,
    isInDialog,
    getRowProps,
  } = props;

  const { table, isResizing, features } = useDataTableSettings<TData>();
  /**
   * We can't use useDataTableQuery here because it's not guaranteed to be available.
   */
  const dataTableQuery =
    useDataTableQuery<unknown>() as DataTableQuery<unknown> | null;
  const isQueryLoading = dataTableQuery?.query?.isLoading ?? false;

  const rows = table.getRowModel().rows;

  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);

  // We don't have stable references, so we create our own key to optimize
  const columnOrderKey = table.getState().columnOrder.join(",");

  const visibleColumnOrder = useMemo(() => {
    const visibleIds = new Set(
      table
        .getVisibleLeafColumns()
        .map((col) => col.id)
        .filter((id) => id !== undefined),
    );
    return table.getState().columnOrder.filter((id) => visibleIds.has(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- is it safe to remove columnOrderKey?
  }, [table, columnOrderKey]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const activeId = String(active.id);
        const overId = String(over.id);

        const pinnedLeftSet = new Set(
          table.getLeftLeafColumns().map((col) => col.id),
        );

        const nextOrder = getNextColumnOrderOnDrag({
          activeId,
          overId,
          pinnedLeftSet,
          currentOrder: table.getState().columnOrder,
          visibleOrder: visibleColumnOrder,
        });

        if (nextOrder) table.setColumnOrder(nextOrder);
      }
      setDraggingColumnId(null);
    },
    [table, visibleColumnOrder],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  // Internal ref required for hooks (useTableScrollAndResize, useTableColumnSizeAndPositionStyle)
  // which must always have an element - many DataTable usages don't pass a ref (e.g. ListContent).
  // When parent passes ref, we merge so they receive the section element too (e.g. for useSyncTableRowHover).
  const sectionRef = useRef<HTMLDivElement>(null);
  const setSectionRef = useCallback(
    (el: HTMLDivElement | null) => {
      (sectionRef as React.MutableRefObject<HTMLDivElement | null>).current =
        el;
      if (ref) {
        if (typeof ref === "function") ref(el);
        else
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [ref],
  );
  const scrollContainerElementRef = useRef<HTMLTableElement>(null);
  const windowScrollRef = useRef<HTMLElement>(null);
  const scrollElementRef = isInDialog
    ? scrollContainerElementRef
    : windowScrollRef;

  useTableScrollAndResize<{ id: string }>({
    tableContainerRef: sectionRef,
    onScroll,
    onResize,
    table,
  });

  useTableColumnSizeAndPositionStyle(table, sectionRef);

  const { store, abortSelection, copySelectionToClipboard } =
    useTableCopySelection({
      enabled: features.enableCopySelection ?? true,
      table,
      visibleColumnOrder,
      containerRef: sectionRef,
      scrollElementRef,
      horizontalScrollElementRef: scrollContainerElementRef,
    });

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setDraggingColumnId(event.active.id as string);
      abortSelection();
    },
    [abortSelection],
  );

  const copySelectionContextValue = useMemo(() => ({ store }), [store]);

  const { handleContextMenu, contextMenu } = useDataTableContextMenu({
    store,
    copySelectionToClipboard,
  });

  const autoScroll = useMemo(() => {
    return {
      enabled: false,
      layoutShiftCompensation: false,
    };
  }, []);

  const modifiers = useMemo(() => {
    return [restrictToHorizontalAxis];
  }, []);

  return (
    <ColumnResizeHandleProvider>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={modifiers}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        sensors={sensors}
        autoScroll={autoScroll}
      >
        <section
          ref={setSectionRef}
          className={cn("whitespace-nowrap grow h-full min-w-0", className)}
          data-testid="data-table-scroll-container"
          tabIndex={-1}
          onContextMenu={handleContextMenu}
        >
          <CopySelectionProvider value={copySelectionContextValue}>
            <DataTableDraggedColumnOverlay
              draggingColumnId={draggingColumnId}
            />
            <DataTableResizedColumnOverlay />
            {features.enableActiveFiltersBar ? (
              <DataTableActiveFiltersBar />
            ) : null}
            <UITable
              className={cn("grid w-full min-h-0 table-fixed", {
                "h-auto": rows.length === 0,
                "h-full": rows.length > 0,
              })}
              wrapperClassName={cn("min-h-0 bg-slate-200 shadow-inner", {
                "h-auto": rows.length === 0,
                "flex-1": rows.length > 0,
              })}
              ref={scrollContainerElementRef}
            >
              <SortableContext
                items={visibleColumnOrder}
                strategy={horizontalListSortingStrategy}
              >
                <DataTableHeader table={table} />
                {isResizing || draggingColumnId ? (
                  <MemoizedDataTableBody
                    table={table}
                    scrollContainerElementRef={scrollContainerElementRef}
                  />
                ) : (
                  <DataTableBody
                    {...props}
                    table={table}
                    scrollContainerElementRef={scrollContainerElementRef}
                    onRowMouseEnter={onRowMouseEnter}
                    onRowMouseLeave={onRowMouseLeave}
                    getRowProps={getRowProps}
                    isInDialog={isInDialog}
                  />
                )}
                {footer}
              </SortableContext>
            </UITable>
            {rows.length === 0 && !isQueryLoading && (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <EmptyState />
              </div>
            )}
          </CopySelectionProvider>
        </section>
      </DndContext>
      {contextMenu}
    </ColumnResizeHandleProvider>
  );
});

export default DataTable;
